import * as React from 'react';
import { Alert, Box, Button, Chip, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import {
    approveBetupFeedPost, BETUP_FEED_LABELS, getBetupFeedUserModerationSummary, getBetupFeedUserPosts, rejectBetupFeedPost,
} from '../../Api';
import {
    BetupFeedAdminPostDto, BetupFeedReviewCategory, BetupFeedUserModerationSummaryDto,
} from '../../models/dtos/BetupFeedPostDto';
import {
    CategoryChips, ConfirmDialog, FeedImage, formatCount, formatFeedDate, LinkedEntity, loadErrorText, PostText,
    RejectPostDialog, ReviewedByChip, ReviewStatusChip, shorten,
} from './BetupFeedShared';

type PendingAction = { kind: 'approve' | 'reject'; row: BetupFeedAdminPostDto } | null;

function SummaryItem({ label, value, secondary, color }: {
    label: string;
    value: React.ReactNode;
    secondary?: React.ReactNode;
    color?: string;
}) {
    return (
        <Box sx={{ minWidth: 88 }}>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
            <Typography variant="h6" sx={{ lineHeight: 1.2, color }}>{value}</Typography>
            {secondary && <Typography variant="caption" color="text.secondary">{secondary}</Typography>}
        </Box>
    );
}

/** The user's moderation counts: posts by review status, reports about them, and how many players blocked / hid them. */
export function UserModerationSummaryStrip({ summary }: { summary: BetupFeedUserModerationSummaryDto | null }) {
    if (summary == null) return null;
    const { posts, post_reports: postReports, message_reports: messageReports } = summary;
    return (
        <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Stack direction="row" gap={2.5} flexWrap="wrap" useFlexGap>
                <SummaryItem label="Pending" value={formatCount(posts?.pending)} color={(posts?.pending ?? 0) > 0 ? 'warning.main' : undefined} />
                <SummaryItem label="Approved" value={formatCount(posts?.approved)} />
                <SummaryItem label="Rejected" value={formatCount(posts?.rejected)} color={(posts?.rejected ?? 0) > 0 ? 'error.main' : undefined} />
                <SummaryItem label="Deleted" value={formatCount(posts?.deleted)} />
                <SummaryItem label="Post reports" value={`${formatCount(postReports?.open)} open`}
                    secondary={`of ${formatCount(postReports?.total)}`}
                    color={(postReports?.open ?? 0) > 0 ? 'error.main' : undefined} />
                <SummaryItem label="Message reports" value={`${formatCount(messageReports?.open)} open`}
                    secondary={`of ${formatCount(messageReports?.total)}`}
                    color={(messageReports?.open ?? 0) > 0 ? 'error.main' : undefined} />
                <SummaryItem label="Blocked by" value={formatCount(summary.blocked_by_count)} secondary="players" />
                <SummaryItem label="Hidden by" value={formatCount(summary.hidden_by_count)} secondary="players" />
            </Stack>
        </Paper>
    );
}

/** User page tab: the user's BETUP Feed posts in every review state, with approve / reject overrides. */
export default function UserFeedPostsTab({ userId }: { userId: number }) {
    const [rows, setRows] = React.useState<BetupFeedAdminPostDto[]>([]);
    const [rowCount, setRowCount] = React.useState(0);
    const [summary, setSummary] = React.useState<BetupFeedUserModerationSummaryDto | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [paginationModel, setPaginationModel] = React.useState<GridPaginationModel>({ page: 0, pageSize: 20 });
    const [error, setError] = React.useState<string | null>(null);
    const [notice, setNotice] = React.useState<string | null>(null);
    const [pending, setPending] = React.useState<PendingAction>(null);
    const [busy, setBusy] = React.useState(false);

    const loadSummary = React.useCallback(async () => {
        const result = await getBetupFeedUserModerationSummary(userId);
        setSummary(result);
        if (result == null) {
            setError(loadErrorText(BETUP_FEED_LABELS.userModerationSummary, 'Failed to load the moderation summary.'));
        }
    }, [userId]);

    const loadPosts = React.useCallback(async () => {
        setLoading(true);
        const page = await getBetupFeedUserPosts(userId, paginationModel.page * paginationModel.pageSize, paginationModel.pageSize);
        setLoading(false);
        if (page == null) {
            setError(loadErrorText(BETUP_FEED_LABELS.userPosts, 'Failed to load posts.'));
            setRows([]);
            setRowCount(0);
            return;
        }
        setRows(page.items ?? []);
        setRowCount(page.totalItems ?? 0);
    }, [userId, paginationModel]);

    React.useEffect(() => { loadSummary(); }, [loadSummary]);
    React.useEffect(() => { loadPosts(); }, [loadPosts]);

    const runAction = async (reason?: string, categories?: BetupFeedReviewCategory[]) => {
        if (pending == null) return;
        const { kind, row } = pending;
        setPending(null);
        setBusy(true);
        setNotice(null);
        const postId = row.post.id;
        const result = kind === 'approve'
            ? await approveBetupFeedPost(postId)
            : await rejectBetupFeedPost(postId, reason ?? '', categories ?? []);
        setBusy(false);
        if (result.error != null) {
            setError(`Could not ${kind} post #${postId}: ${result.error}`);
            return;
        }
        setError(null);
        setNotice(kind === 'approve'
            ? `Post #${postId} approved and published at the top of the feed.`
            : `Post #${postId} rejected — the author sees the reason and can no longer edit it.`);
        await Promise.all([loadPosts(), loadSummary()]);
    };

    const columns: GridColDef<BetupFeedAdminPostDto>[] = [
        {
            field: 'created_at', headerName: 'Created', width: 160, sortable: false,
            renderCell: ({ row }) => (
                <Stack>
                    <Typography variant="body2">{formatFeedDate(row.post.created_at)}</Typography>
                    <Typography variant="caption" color="text.secondary">post #{row.post.id}</Typography>
                    {row.post.edited_at && (
                        <Typography variant="caption" color="text.secondary">edited {formatFeedDate(row.post.edited_at)}</Typography>
                    )}
                </Stack>
            ),
        },
        {
            field: 'text', headerName: 'Text', flex: 1, minWidth: 260, sortable: false,
            renderCell: ({ row }) => (
                <Stack gap={1} sx={{ width: '100%' }}>
                    <PostText text={row.post.text} />
                    <LinkedEntity match={row.post.match} team={row.post.team} />
                </Stack>
            ),
        },
        {
            field: 'image', headerName: 'Image', width: 116, sortable: false,
            renderCell: ({ row }) => <FeedImage url={row.post.image_url} expired={row.post.image_expired} />,
        },
        {
            field: 'review_status', headerName: 'Review', width: 230, sortable: false,
            renderCell: ({ row }) => (
                <Stack gap={0.75}>
                    <Stack direction="row" gap={0.5} flexWrap="wrap">
                        <ReviewStatusChip status={row.review_status} />
                        <ReviewedByChip reviewer={row.reviewed_by} />
                        {(row.open_reports_count ?? 0) > 0 && (
                            <Chip size="small" color="error" label={`${row.open_reports_count} open report${row.open_reports_count === 1 ? '' : 's'}`} />
                        )}
                        {row.hidden && <Chip size="small" variant="outlined" label="hidden" />}
                        {row.deleted && <Chip size="small" variant="outlined" color="error" label="deleted" />}
                    </Stack>
                    <Typography variant="caption" color="text.secondary">reviewed {formatFeedDate(row.reviewed_at)}</Typography>
                    <CategoryChips categories={row.review_categories} />
                    {row.review_reason && (
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{row.review_reason}</Typography>
                    )}
                </Stack>
            ),
        },
        {
            field: 'actions', headerName: 'Actions', width: 140, sortable: false,
            renderCell: ({ row }) => row.deleted ? null : (
                <Stack gap={1}>
                    {row.review_status !== 'APPROVED' && (
                        <Button size="small" variant="contained" color="success" disabled={busy}
                            onClick={() => setPending({ kind: 'approve', row })}>
                            {row.review_status === 'REJECTED' ? 'Approve anyway' : 'Approve'}
                        </Button>
                    )}
                    {row.review_status !== 'REJECTED' && (
                        <Button size="small" variant="outlined" color="error" disabled={busy}
                            onClick={() => setPending({ kind: 'reject', row })}>Reject</Button>
                    )}
                </Stack>
            ),
        },
    ];

    const subject = pending == null ? '' : (
        <Tooltip title={pending.row.post.text}>
            <span>Post #{pending.row.post.id}: “{shorten(pending.row.post.text)}”</span>
        </Tooltip>
    );

    return (
        <Stack gap={2} sx={{ mt: 2 }}>
            <UserModerationSummaryStrip summary={summary} />
            <Typography variant="body2" color="text.secondary">
                All of the user's posts, newest first. Approving a rejected post publishes it at the top of the feed;
                rejecting an approved one takes it off the feed with your reason and categories.
            </Typography>
            {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
            {notice && <Alert severity="success" onClose={() => setNotice(null)}>{notice}</Alert>}
            <DataGrid
                autoHeight
                rows={rows}
                columns={columns}
                getRowId={row => row.post.id}
                getRowHeight={() => 'auto'}
                paginationMode="server"
                rowCount={rowCount}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[10, 20, 50]}
                loading={loading}
                disableRowSelectionOnClick
                disableColumnMenu
                sx={{ '& .MuiDataGrid-cell': { py: 1.5, alignItems: 'flex-start' } }}
                localeText={{ noRowsLabel: 'No feed posts' }}
            />
            <ConfirmDialog
                open={pending?.kind === 'approve'}
                title="Approve post?"
                confirmLabel={pending?.row.review_status === 'REJECTED' ? 'Approve anyway' : 'Approve'}
                confirmColor="success"
                onClose={() => setPending(null)}
                onConfirm={() => runAction()}
                text={pending == null ? '' : (
                    <>
                        {subject}
                        <br />
                        {pending.row.review_status === 'REJECTED'
                            ? `Overrides the rejection${pending.row.reviewed_by === 'MODEL' ? ' by Claude' : ''}: the post is published at the top of the feed (unless it is hidden) and the author sees it approved.`
                            : 'The post will be published at the top of the feed (unless it is hidden).'}
                    </>
                )}
            />
            <RejectPostDialog
                open={pending?.kind === 'reject'}
                subject={pending == null ? '' : (
                    <>
                        {subject}
                        {pending.row.review_status === 'APPROVED' && (
                            <><br />The post is live now — rejecting removes it from the feed.</>
                        )}
                    </>
                )}
                initialCategories={pending?.row.review_categories as string[] | null | undefined}
                onClose={() => setPending(null)}
                onConfirm={(reason, categories) => runAction(reason, categories)}
            />
        </Stack>
    );
}
